import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BoxModelConstraint, BreakableRenderer, ColumnComposition, DotRenderer, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { ColorsConfig } from '@ddt-web/config';
import { CellRow, TabData } from '@ddt-web/data-service';
import { ReportType } from '@ddt-web/pdf-utils';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

import { PerformanceColumnComposition } from '../column-compositions/performance-column-composition';
import { ReducedSpacingConfig } from '../config';

export class FundPerformancePanelsHelper {

	private static readonly reducedSpacingConfig = ReducedSpacingConfig.fundPerformancePanelHelper;

	public static getFundPerformancePanels(
		tabData: TabData,
		contentSvc: ContentService,
		rowPanelConstraints: Partial<BoxModelConstraint> = {},
		mode?: ReportType,
		shouldReduceSpacing: boolean = false
	): Array<AbstractRenderer<SVGGElement>> {
		const rows: Array<RowPanel> = [];
		const columnConfigurations = new PerformanceColumnComposition().getFundPerfColumnCompositions(tabData, mode, contentSvc);
		const name = get(tabData, 'config.title.label.key');

		if (!columnConfigurations || !columnConfigurations.headers || !columnConfigurations.body) {
			console.error(`Column configurations are not defined, table: ${name} will not render!`);
			return;
		}
		const headerConstraints = shouldReduceSpacing ?
			FundPerformancePanelsHelper.reducedSpacingConfig.headerRowPanel
			: { paddingBottom: 6, borderBottom: 0.25, borderBottomColor: '#000', marginBottom: 10 };
		const headerRow: RowPanel = new RowPanel(
			`${name} Header Row`,
			columnConfigurations.headers.map((config: ColumnComposition) => config.renderer.render()),
			headerConstraints,
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);
		rows.push(headerRow);

		const bodyConstraints = shouldReduceSpacing ?
			FundPerformancePanelsHelper.reducedSpacingConfig.body : { paddingBottom: 6 };
		columnConfigurations.body.forEach((configs: Array<ColumnComposition>) => {
			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
			rows.push(new RowPanel(`${name} Body Row`, rowRenderer, bodyConstraints, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
		});
		return [new BreakableRenderer(rows, rowPanelConstraints, headerRow)];
	}

	public static getAsOfDatePanel(
		tabData: TabData,
		rowPanelConstraints: Partial<BoxModelConstraint> = { paddingBottom: 10 },
		shouldReduceSpacing: boolean = false
	): AbstractRenderer<SVGGElement>[] {
		const panels = [];
		if (shouldReduceSpacing) {
			rowPanelConstraints = FundPerformancePanelsHelper.reducedSpacingConfig.asOfDatePanel;
		}

		const asOfDateIndex: number = get(tabData, 'tableData[0].headersX', []).findIndex(header => header.rawValue === 'As of Date');
		const asOfDate: string = get(tabData, `tableData[0].cells[0][${asOfDateIndex}].formattedValue`);

		if (!isNil(asOfDate)) {
			panels.push(
				new StaticParagraphComponent('AS_OF', 'As of {0}', { 0: asOfDate }, rowPanelConstraints)
			);
		}

		return panels;
	}

	public static getPerformanceLegendPanels(portfolioData: CellRow[]): Array<AbstractRenderer<SVGGElement>> {
		return portfolioData.map(
			(row: CellRow, index: number) =>
				new RowPanel(
					'Legend and portfolio name',
					[new DotRenderer(ColorsConfig.PORTFOLIO_COLORS[index], { paddingRight: 10 }, { size: 10 }), new StaticParagraphComponent(null, get(row, `field['name'].formattedValue`))],
					{ paddingBottom: 8 },
					Alignment.Horizontal.Left
				)
		);
	}
}
